import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import DatePicker from 'react-date-picker';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { toDateValue, toIsoDate } from '@utils/dateRange';
import { FilterGroup, GroupLabel, SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { useUrlFilters } from '@hooks/application/useUrlFilters';
import { useDebouncedSearch } from './useDebouncedSearch';
import { useAuditEvents } from './useAuditEvents';
import { AUDIT_TABLE_MIN_WIDTH, AuditEventHeader, AuditEventRow } from './AuditEventRow';
import { Pager } from './Pager';
import { GateNotice } from './GateNotice';
import { NoticeText, NOTICE_TONES } from './NoticeText';
import { ACTOR_TYPE_FILTERS, CATALOG_TABS, auditUrl, fmdcPaths } from './fmdcUrl';
import { FilterRow, FilterSelect, SearchInput, Table, TableScroll } from './styles';
import { humanizeToken, formatCount } from './format';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';

// Every action on the client, newest first, with its actor. Filters live in the URL (auditUrl), so
// "the audit for run 4" is a link a run detail can hand out.

const ACTOR_SEGMENTS = [
  { key: ACTOR_TYPE_FILTERS.ALL, label: 'All' },
  { key: ACTOR_TYPE_FILTERS.USER, label: 'User' },
  { key: ACTOR_TYPE_FILTERS.ADMIN_USER, label: 'Admin' },
  { key: ACTOR_TYPE_FILTERS.SYSTEM, label: 'System' },
];

// The picker's theme wrap, from SpecList: the library ships its own light styles, and these put the
// theme's colours over them.
const DatePickerWrapper = styled.div`
  .react-date-picker__wrapper {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    border-radius: 8px;
    padding: 8px;
    height: 40px;
    box-sizing: border-box;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }

  .react-date-picker__inputGroup__input {
    color: ${({ theme }) => theme.themeColors.text};
  }

  .react-calendar {
    background: ${({ theme }) => theme.themeColors.appBackground};
    color: ${({ theme }) => theme.themeColors.text};
  }

  .react-calendar button {
    background: ${({ theme }) => theme.themeColors.appBackground} !important;
    color: ${({ theme }) => theme.themeColors.text} !important;
  }

  .react-calendar button:enabled:hover {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
    color: ${({ theme }) => theme.themeColors.buttonText} !important;
  }

  .react-calendar__tile--active {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
    color: ${({ theme }) => theme.themeColors.buttonText} !important;
  }
`;

const isFiltering = (filters) =>
  !!filters.runId ||
  !!filters.eventType ||
  filters.actorType !== auditUrl.defaults.actorType ||
  !!filters.from ||
  !!filters.to;

// Where an event's subject lives in this area, by subject type. Rows and unmapped rows need the
// event's run id to make a path; a type nobody here knows gets no link.
// Where an audit subject lives, per the backend's subject types: run, extraction_row,
// unmapped_row, tenant, catalog_item (whose id is always null — the code is in before/after, so it
// links to the catalog without a search), catalog_proposal, issuer_mapping (which belongs to its
// run), and a null subject for catalog_refreshed.
export const subjectPath = (subject, runId, base = ROUTES.fmdc) => {
  if (!subject?.type) return null;
  const hasId = subject.id !== undefined && subject.id !== null;
  switch (subject.type) {
    case 'run':
      return hasId ? fmdcPaths.run(base, subject.id) : null;
    case 'extraction_row':
      return runId && hasId ? fmdcPaths.value(base, runId, subject.id) : null;
    case 'unmapped_row':
      return runId && hasId ? fmdcPaths.unmapped(base, runId, subject.id) : null;
    case 'issuer_mapping':
      return runId ? fmdcPaths.run(base, runId) : null;
    case 'catalog_item':
      return hasId ? `${fmdcPaths.catalog(base)}?q=${encodeURIComponent(subject.id)}` : fmdcPaths.catalog(base);
    case 'catalog_proposal':
    case 'proposal':
      return `${fmdcPaths.catalog(base)}?tab=${CATALOG_TABS.PROPOSALS}&proposals=all`;
    case 'tenant':
      return fmdcPaths.settings(base);
    default:
      return null;
  }
};

export const AuditView = () => {
  const navigate = useNavigate();
  const { filters, setFilters } = useUrlFilters(auditUrl);
  const { events, total, isLoading, error, reload } = useAuditEvents(filters);

  // The run id box is bound locally and written to the URL after a pause.
  const [runIdText, setRunIdText] = useDebouncedSearch(filters.runId, (text) => setFilters({ runId: text.trim() }));

  // Event types offered: whatever this page carries, plus the one the URL already holds, so a
  // filter never offers a type with no rows and never loses the one it is narrowed to.
  const eventTypes = React.useMemo(() => {
    const seen = new Set((events || []).map((e) => e.event_type).filter(Boolean));
    if (filters.eventType) seen.add(filters.eventType);
    return Array.from(seen).sort();
  }, [events, filters.eventType]);

  const openSubject = (subject, runId) => {
    const path = subjectPath(subject, runId);
    if (path) navigate(path);
  };

  const clearFilters = () => {
    setRunIdText('');
    setFilters({ runId: '', eventType: '', actorType: auditUrl.defaults.actorType, from: null, to: null });
  };

  const renderBody = () => {
    if (isLoading) return <Body5 isLowContrast>Loading audit trail…</Body5>;
    if (error && (!events || events.length === 0)) {
      return (
        <div>
          <Body5>{error}</Body5>
          <Container top={12}>
            <SkeletonButton onClick={reload}>Retry</SkeletonButton>
          </Container>
        </div>
      );
    }
    if (events.length === 0) {
      return isFiltering(filters) ? (
        <GateNotice compact title="No audit events match" body="Try a wider window or another actor.">
          <TransparentFlatButton onClick={clearFilters}>Clear filters</TransparentFlatButton>
        </GateNotice>
      ) : (
        <GateNotice
          compact
          title="No activity yet"
          body="Uploads, decisions and catalog edits appear here with their actor."
        />
      );
    }
    return (
      <>
        {error && (
          <Container bottom={12}>
            <NoticeText tone={NOTICE_TONES.ERROR}>{error}</NoticeText>
          </Container>
        )}
        <TableScroll>
          <Table $minWidth={AUDIT_TABLE_MIN_WIDTH}>
            <AuditEventHeader />
            {events.map((event) => (
              <AuditEventRow
                key={event.id}
                event={event}
                onOpenRun={(runId) => navigate(fmdcPaths.run(ROUTES.fmdc, runId))}
                onOpenSubject={
                  subjectPath(event.subject, event.run_id) ? (subject) => openSubject(subject, event.run_id) : undefined
                }
              />
            ))}
          </Table>
        </TableScroll>
        <Pager
          offset={filters.offset}
          total={total}
          count={events.length}
          onChange={(offset) => setFilters({ offset })}
        />
      </>
    );
  };

  return (
    <div>
      <FilterRow>
        <FilterGroup>
          <GroupLabel as="label" htmlFor="fmdc-audit-run">
            Run id
          </GroupLabel>
          <SearchInput
            id="fmdc-audit-run"
            aria-label="Filter by run id"
            inputMode="numeric"
            value={runIdText}
            onChange={(e) => setRunIdText(e.target.value.replace(/[^\d]/g, ''))}
            style={{ minWidth: 120, width: 120 }}
          />
        </FilterGroup>
        <FilterGroup>
          <GroupLabel as="label" htmlFor="fmdc-audit-event">
            Event
          </GroupLabel>
          <FilterSelect
            id="fmdc-audit-event"
            aria-label="Filter by event type"
            value={filters.eventType}
            onChange={(e) => setFilters({ eventType: e.target.value })}
          >
            <option value="">All events</option>
            {eventTypes.map((type) => (
              <option key={type} value={type}>
                {humanizeToken(type)}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
        <SegmentedControl
          label="Actor"
          options={ACTOR_SEGMENTS}
          active={filters.actorType}
          onSelect={(actorType) => setFilters({ actorType })}
        />
        <FilterGroup>
          <GroupLabel as="label" htmlFor="fmdc-audit-from">
            From
          </GroupLabel>
          <DatePickerWrapper>
            <DatePicker
              id="fmdc-audit-from"
              name="audit-from"
              aria-label="Events from"
              format="y-MM-dd"
              value={toDateValue(filters.from)}
              onChange={(date) => setFilters({ from: toIsoDate(date) })}
            />
          </DatePickerWrapper>
        </FilterGroup>
        <FilterGroup>
          <GroupLabel as="label" htmlFor="fmdc-audit-to">
            To
          </GroupLabel>
          <DatePickerWrapper>
            <DatePicker
              id="fmdc-audit-to"
              name="audit-to"
              aria-label="Events to"
              format="y-MM-dd"
              value={toDateValue(filters.to)}
              onChange={(date) => setFilters({ to: toIsoDate(date) })}
            />
          </DatePickerWrapper>
        </FilterGroup>
      </FilterRow>

      {!isLoading && events && events.length > 0 && (
        <Container top={12} row verticallyCenter gap={12}>
          <Body7 isLowContrast role="status">
            {formatCount(total)} event{total === 1 ? '' : 's'}
          </Body7>
          {isFiltering(filters) && <TransparentFlatButton onClick={clearFilters}>Clear filters</TransparentFlatButton>}
        </Container>
      )}

      <Container top={16}>{renderBody()}</Container>
    </div>
  );
};
